import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { updateExhibitionContent, getExhibitionContentById, getExhibitions } from './request';
import { useAuth } from '../context/AuthContext';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

const EditExhibitionContent = () => {

  const navigate = useNavigate();
  const { authToken } = useAuth();
  const location = useLocation();  
  const { contentId } = location.state; 
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  const [exhibitions, setExhibitions] = useState([]); 
  const [exhibitionId, setExhibitionId] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const resp = await getExhibitionContentById(authToken, contentId);
        if(resp.Result){
          const data = resp.Content;
        setTitle(data.title);
        setContent(data.content);
        setExhibitionId(data.exhibitionId);
        }
      } catch (err) {
        setError('Failed to fetch content data');
      }
    };
    const fetchExhibitions = async () => {
      try {
        const response = await getExhibitions(authToken);
        if (response.Result) {
          setExhibitions(response.Exhibitions);
        }
      } catch (error) {
        console.error('Error fetching exhibitions:', error);
      }
    };
    fetchContent();
    fetchExhibitions();
  }, [contentId, authToken]);

  const handleTitleChange = (e) => setTitle(e.target.value);
  const handleContentChange = (value) => setContent(value);
  const handleExhibitionChange = (e) => setExhibitionId(e.target.value);

  const handleSubmit = async () => {
    if (!title || !content || !exhibitionId) {
      setError('Please fill out all fields');
      return;
    }
    setError('');
    const data = { title, content, exhibitionId, category: 'exhibition' };
    try {
      setLoading(true);
      const response = await updateExhibitionContent(authToken, contentId, data);
      if (response.Result) {
        alert('Content updated successfully!');
        navigate('/exhibitioncontent');
      } else {
        setError('Failed to update the content. Please try again.');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
      console.error('Error updating content:', error);
    } finally {
      setLoading(false);
    }
  };
  const modules = {
  toolbar: [
    [{ header: 1 }, { header: 2 }, { header: 3 }, { header: 4 }, { font: [] }], // Headers and fonts
    [{ size: [] }], // Font sizes
    ['bold', 'italic', 'underline', 'strike'], // Text styles
    [{ color: [] }, { background: [] }], // Colors
    [{ list: 'ordered' }, { list: 'bullet' }], // Lists
    [{ align: [] }], // Alignment
    ['image'], // Add this for image insertion
    ['clean'], // Remove formatting
  ],
};
  
  const formats = [
    'header', 'font', 'size', 'bold', 'italic', 'underline',
    'color', 'background', 'list', 'bullet', 'align', 'image'
  ];
  return (
    <div className="sm:px-6 lg:px-8">
      <Button
        variant="contained"
        color="primary"
        onClick={() => navigate('/exhibitioncontent')}
        disabled={loading}
      >
        {loading ? <CircularProgress size={20} color="inherit" /> : 'Go Back'}
      </Button>
      <div className="w-full h-fit bg-white p-6 rounded-lg shadow-lg">
        {/* Page Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold">Edit Exhibition Content</h2>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Update Content'}
          </Button>
        </div>

        {/* Content Form */}
        <div>
          {error && (
            <div className="text-red-500 text-sm mb-4">{error}</div>
          )}
          {/* Title Input */}
          <div className="mb-6">
            <label htmlFor="title" className="block text-sm font-semibold mb-2">
              Title
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={handleTitleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="Enter content title"
            />
          </div>
          {/* Exhibition Select */}
          <div className="mb-6">
            <label htmlFor="exhibition" className="block text-sm font-semibold mb-2">
              Exhibition Year
            </label>
            <Select
              value={exhibitionId}
              onChange={handleExhibitionChange}
              fullWidth
            >
              <MenuItem value="">Select Exhibition Year</MenuItem>
              {exhibitions.map((exh) => (
                <MenuItem key={exh.id} value={exh.id}>
                  {exh.year}
                </MenuItem>
              ))}
            </Select>
          </div>

          {/* Quill Editor */}
          <div className="border-none">
            <label htmlFor="content" className="block text-sm font-semibold mb-2">
              Content
            </label>
            <div className='mb-14'>
              <ReactQuill
                value={content}
                onChange={handleContentChange}
                modules={modules}
                formats={formats}
                className="h-52"
                placeholder="Write your exhibition content here"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditExhibitionContent;